import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList, type PagedListParams} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'

export type MatrizLegal = components['schemas']['MatrizLegalRead']
export type LegalItem = components['schemas']['LegalItemRead']
export type Novedad = components['schemas']['NovedadRead']
export type LegalItemEstado = components['schemas']['LegalItemEstado']
export type CrearMejoraLegal = components['schemas']['CrearMejoraLegalRead']
export type NormaPropiaPayload = components['schemas']['NormaPropiaPayload']
export type MejoraEditada = components['schemas']['MejoraConfigurable']

export type PaginaRegistro = components['schemas']['PaginaRegistro']
export type PaginaNovedades = components['schemas']['PaginaNovedades']

export const MATRIZ_LEGAL_PAGE_SIZE = 15

/** Trae los huecos de dato (sin clase de riesgo, sin CIIU) en la misma lectura. `extra.ancla`
 * pide la página donde cae esa norma, no la primera. */
export function useRegistroLegal(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<LegalItem, PaginaRegistro>(
    ['matriz-legal', businessId, 'registro'],
    businessId ? `/businesses/${businessId}/sgsst/matriz-legal/registro` : undefined,
    params,
    MATRIZ_LEGAL_PAGE_SIZE
  )
}

/** Trae el veredicto del portón de diseño: quien mira necesita saber si puede incorporar antes. */
export function useNovedadesLegal(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<Novedad, PaginaNovedades>(
    ['matriz-legal', businessId, 'novedades'],
    businessId ? `/businesses/${businessId}/sgsst/matriz-legal/novedades` : undefined,
    params,
    MATRIZ_LEGAL_PAGE_SIZE
  )
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>, businessId: string) {
  queryClient.invalidateQueries({queryKey: ['matriz-legal', businessId]})
  // El registro respalda el estándar 2.7.1: un cambio puede mover su sugerido.
  queryClient.invalidateQueries({queryKey: ['sgsst', businessId]})
}

export function useIncorporarNovedad(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (normRef: string) =>
      (
        await api.post<LegalItem>(`/businesses/${businessId}/sgsst/matriz-legal/novedades/incorporar`, {
          norm_ref: normRef,
        })
      ).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function useDescartarNovedad(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({normRef, motivo}: {normRef: string; motivo: string}) =>
      (
        await api.post<MatrizLegal>(`/businesses/${businessId}/sgsst/matriz-legal/novedades/descartar`, {
          norm_ref: normRef,
          motivo,
        })
      ).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function useDesincorporarItem(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (normRef: string) =>
      (
        await api.post<MatrizLegal>(`/businesses/${businessId}/sgsst/matriz-legal/registro/desincorporar`, {
          norm_ref: normRef,
        })
      ).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function usePatchLegalItem(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      itemId,
      estado,
      justificacion,
      notas,
    }: {
      itemId: string
      estado?: LegalItemEstado
      justificacion?: string
      notas?: string
    }) =>
      (
        await api.patch<LegalItem>(`/businesses/${businessId}/sgsst/matriz-legal/items/${itemId}`, {
          estado,
          justificacion,
          notas,
        })
      ).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

/** Idempotente: si ya hay una acción abierta ligada, el backend contesta esa (`ya_existia`). */
export function useCrearMejoraLegal(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({itemId, editado}: {itemId: string; editado?: MejoraEditada}) =>
      (
        await api.post<CrearMejoraLegal>(
          `/businesses/${businessId}/sgsst/matriz-legal/items/${itemId}/crear-mejora`,
          editado ?? {}
        )
      ).data,
    onSuccess: () => {
      invalidate(queryClient, businessId)
      queryClient.invalidateQueries({queryKey: ['actions', businessId]})
      queryClient.invalidateQueries({queryKey: ['plan-propuesta', businessId]})
    },
  })
}

/** Declarar es incorporar: la norma queda en el registro en el mismo acto. */
export function useDeclararNormaPropia(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: NormaPropiaPayload) =>
      (await api.post<LegalItem>(`/businesses/${businessId}/sgsst/matriz-legal/propias`, payload)).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export function useEditarNormaPropia(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({propiaId, ...payload}: NormaPropiaPayload & {propiaId: string}) =>
      (await api.patch<LegalItem>(`/businesses/${businessId}/sgsst/matriz-legal/propias/${propiaId}`, payload)).data,
    onSuccess: () => invalidate(queryClient, businessId),
  })
}

export type RiesgoLigado = components['schemas']['RiesgoLigadoRead']

/** Endpoint propio y no el GET de la matriz: ese además re-siembra el perfil de riesgo, algo
 * que abrir un desplegable no debe disparar. */
export function useRiesgosLigables(businessId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['riesgos-ligables', businessId],
    queryFn: async () =>
      (await api.get<RiesgoLigado[]>(`/businesses/${businessId}/sgsst/matriz-legal/riesgos-ligables`)).data,
    enabled: !!businessId && enabled,
  })
}

function invalidateVinculo(queryClient: ReturnType<typeof useQueryClient>, businessId: string) {
  invalidate(queryClient, businessId)
  // La matriz de riesgos repinta sus chips y su «requisito legal», que ligar puede encender.
  queryClient.invalidateQueries({queryKey: ['ipevr', businessId]})
}

export function useLigarRiesgo(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({itemId, filaId}: {itemId: string; filaId: string}) =>
      (
        await api.post<LegalItem>(`/businesses/${businessId}/sgsst/matriz-legal/items/${itemId}/riesgos`, {
          riesgo_fila_id: filaId,
        })
      ).data,
    onSuccess: () => invalidateVinculo(queryClient, businessId),
  })
}

export function useDesligarRiesgo(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({itemId, filaId}: {itemId: string; filaId: string}) =>
      (await api.delete<LegalItem>(`/businesses/${businessId}/sgsst/matriz-legal/items/${itemId}/riesgos/${filaId}`))
        .data,
    onSuccess: () => invalidateVinculo(queryClient, businessId),
  })
}

export function matrizLegalExportUrl(businessId: string): string {
  return `/businesses/${businessId}/sgsst/matriz-legal/export.xlsx`
}
